import { useEffect, useCallback } from "react";
import { useAppDispatch, useAppSelector } from "@redux/hooks";
import { useExternalProviderRepository } from "../context/external-provider-repository.context";
import { useToast } from "@components/ui/use-toast";
import type { AppState } from "@redux/app-state";
import type { RequestState } from "@redux/state/shared-types";
import type { SubmitInvoiceBody } from "@shared-kernel/application/ports/external-provider/external-provider-repository";
import type { Nullable } from "@shared-kernel/core/types/nullable";
import { uploadExternalProviderInvoice } from "@academy-context/write/application/use-cases/external-provider/invoice-upload/upload-external-provider-invoice";
import {
  submitExternalProviderInvoice,
  resetSubmitExternalProviderInvoice,
} from "@academy-context/write/application/use-cases/external-provider/invoice-submission/submit-external-provider-invoice";

import type { SubmittedInvoiceData } from "@academy-context/write/application/use-cases/external-provider/invoice-submission/submit-external-provider-invoice";

const selectExternalProviderInvoiceUpload = (state: AppState) => state.externalProvider.invoiceUpload;
const selectExternalProviderInvoiceSubmission = (state: AppState) => state.externalProvider.invoiceSubmission;

export interface UseInvoiceSubmitResult {
  uploadFile: (file: File) => Promise<string | null>;
  submitInvoice: (participationId: string, body: SubmitInvoiceBody) => Promise<boolean>;
  uploadProcessing: RequestState;
  submitProcessing: RequestState;
  invoiceSubmittedSuccessfully: boolean;
  submittedInvoiceData: Nullable<SubmittedInvoiceData>;
}

export const useInvoiceSubmit = (): UseInvoiceSubmitResult => {
  const dispatch = useAppDispatch();
  const repository = useExternalProviderRepository();
  const { toast } = useToast();

  const { processing: uploadProcessing } = useAppSelector(selectExternalProviderInvoiceUpload);
  const { processing: submitProcessing, submittedInvoiceData } = useAppSelector(selectExternalProviderInvoiceSubmission);

  useEffect(() => {
    if (submitProcessing === "success") {
      toast({ description: "Facture soumise avec succès !" });
    } else if (submitProcessing === "failed") {
      toast({
        description: "Erreur lors de la soumission de la facture",
        variant: "destructive",
      });
      dispatch(resetSubmitExternalProviderInvoice());
    }
  }, [dispatch, submitProcessing, toast]);

  const uploadFile = useCallback(
    async (file: File): Promise<string | null> => {
      try {
        const url = await dispatch(uploadExternalProviderInvoice({ file, externalProviderRepository: repository })).unwrap();
        toast({ description: "Fichier téléchargé avec succès" });
        return url;
      } catch {
        toast({
          description: "Erreur lors du téléchargement de la facture",
          variant: "destructive",
        });
        return null;
      }
    },
    [dispatch, repository, toast],
  );

  const submitInvoice = useCallback(
    async (participationId: string, body: SubmitInvoiceBody): Promise<boolean> => {
      try {
        await dispatch(submitExternalProviderInvoice({ participationId, body, externalProviderRepository: repository })).unwrap();
        return true;
      } catch {
        return false;
      }
    },
    [dispatch, repository],
  );

  return {
    uploadFile,
    submitInvoice,
    uploadProcessing,
    submitProcessing,
    invoiceSubmittedSuccessfully: submitProcessing === "success",
    submittedInvoiceData,
  };
};
